import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  FileText,
  Bot,
  LogOut,
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  icons,
  FileTextIcon,
  Upload,
  UploadCloudIcon,
  History,
  HistoryIcon,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/utils';
import { useCurrentUser } from '@/hooks/redux';
import { useDispatch } from 'react-redux';
import { logout } from '@/store/slices/authSlice';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutGrid },
  {
    name: 'Training',

    icon: Bot,
    children: [
      { name: 'Upload Doc ', href: '/training', icon: Upload },
      { name: 'Upload Prompt', href: '/upload-prompt', icon: UploadCloudIcon },
    ],
  },
  {
    name: 'File History',
    href: '/file-history',
    icon: FileText,
    children: [
      { name: 'File History ', href: '/file-history', icon: History },
      { name: ' Prompt History', href: '/prompt-history', icon: HistoryIcon },
    ],
  },
];

export function EnhancedSidebar({ isOpen, onToggle }: SidebarProps) {
  const user = useCurrentUser();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login', { replace: true });
  };
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const isChildActive = (children: any[]) => {
    return children.some((child) => location.pathname.startsWith(child.href));
  };

  return (
    <>
      {/* Sidebar Backdrop for Mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/10 backdrop-blur-[2px] lg:hidden transition-opacity"
          onClick={onToggle}
        />
      )}

      {/* Toggle Button - Floating & Dynamic */}
      <button
        onClick={onToggle}
        className={cn(
          'fixed top-5 z-50 flex h-9 w-9 items-center justify-center rounded-full bg-accent text-accent-foreground shadow-md border border-border transition-all duration-300 ease-in-out hover:scale-105 active:scale-95',
          isOpen ? 'left-[275px]' : 'left-4'
        )}
      >
        {isOpen ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
      </button>

      {/* Sidebar Container */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex flex-col bg-card border-r transition-all duration-300 ease-in-out overflow-hidden',
          isOpen ? 'w-80' : 'w-0 border-none'
        )}
      >
        {/* Header / Logo */}
        <div className="flex h-20 items-center px-6 shrink-0">
          <div
            className={cn(
              'flex items-center gap-3 transition-opacity duration-200',
              !isOpen && 'opacity-0'
            )}
          >
            <img src="/icons/favicon.png" className="h-9 w-9 object-contain" alt="logo" />
            <div className="flex flex-col">
              <span className="text-lg font-bold tracking-tight text-foreground uppercase">
                Admin Panel
              </span>
              <span className="text-[10px] text-muted-foreground font-medium -mt-1 uppercase tracking-widest">
                MST Buddy
              </span>
            </div>
          </div>
        </div>

        {/* Navigation - Styled for "Premium" look */}
        <nav className="flex-1 px-3 space-y-1.5 mt-4 overflow-y-auto custom-scrollbar">
          {navigation.map((item) => {
            const Icon = item.icon;

            // 🔹 If item has children
            if (item.children) {
              const isOpenMenu =
                openMenu === item.name || (item.children && isChildActive(item.children));

              return (
                <div key={item.name}>
                  {/* Parent */}
                  <div
                    onClick={() => setOpenMenu(isOpenMenu ? null : item.name)}
                    className="flex items-center justify-between px-4 py-3 rounded-xl cursor-pointer text-sm font-semibold text-muted-foreground hover:bg-accent"
                  >
                    <div className="flex items-center gap-3">
                      <Icon size={20} />
                      <span>{item.name}</span>
                    </div>

                    <ChevronRight
                      size={16}
                      className={cn('transition-transform', isOpenMenu && 'rotate-90')}
                    />
                  </div>

                  {/* Children */}
                  {isOpenMenu && (
                    <div className="ml-8 mt-1 space-y-1">
                      {item.children.map((child) => {
                        const ChildIcon = child.icon;

                        return (
                          <NavLink
                            key={child.name}
                            to={child.href}
                            className={({ isActive }) =>
                              cn(
                                'flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-all',
                                isActive
                                  ? 'text-primary bg-primary/10 border-l-4 border-primary'
                                  : 'text-muted-foreground hover:bg-accent'
                              )
                            }
                          >
                            {/* 🔥 Child Icon */}
                            {ChildIcon && <ChildIcon size={16} />}

                            <span>{child.name}</span>
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            // 🔹 Normal item (no children)
            return (
              <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold',
                    isActive
                      ? 'bg-primary/10 text-primary border-l-4 border-primary'
                      : 'text-muted-foreground hover:bg-accent'
                  )
                }
              >
                <Icon size={20} />
                <span>{item.name}</span>
              </NavLink>
            );
          })}
        </nav>

        {/* User / Footer Section */}
        <div
          className={cn(
            'p-4 border-t border-border bg-muted/30 transition-opacity duration-300',
            !isOpen && 'opacity-0'
          )}
        >
          {user && (
            <div className="flex items-center gap-3 mb-4 px-2">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/20 text-primary border border-primary/20 font-bold">
                {user.name.charAt(0).toUpperCase()}
              </div>
              <div className="flex flex-col min-w-0">
                <p className="text-sm font-bold truncate text-foreground">{user.name}</p>
                <p className="text-[11px] text-muted-foreground truncate">{user.email}</p>
              </div>
            </div>
          )}

          <Button
            title="Logout"
            className={`w-full flex items-center transition-colors ${
              isOpen ? 'left-4 right-4 justify-center' : 'm-0.5 justify-center'
            }`}
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4" />
            {isOpen && <span className="m-2">Logout</span>}
          </Button>
        </div>
      </aside>

      {/* Main Content Spacer: This pushes your dashboard over when sidebar is open */}
      <div
        className={cn(
          'hidden lg:block shrink-0 transition-all duration-300 ease-in-out',
          isOpen ? 'w-80' : 'w-0'
        )}
      />
    </>
  );
}
